import type { Config, Path, Project, Session } from "@mux/sdk/v2/client"
import type { ServerConnection } from "@/context/server"
import { jsonArray, jsonObject } from "@/transport/decode"
import { expectJson } from "@/transport/http"
import { serverFetch } from "@/transport/server"

export type RuntimePath = Path & {
  /** Where Add Workspace starts without a known folder (~/mux on macOS). Empty or absent means home. */
  defaultWorkspaceParent?: string
}

export type RuntimeBootstrap = {
  healthy: boolean
  path: RuntimePath
  projects: Project[]
  config: Config
  /** The workspace whose Oracle is the Mux Oracle; absent on runtimes without one. */
  machineWorkspace?: MachineWorkspace
  /** Each workspace's Oracle chat, so opening an Oracle never waits on a request. */
  oracles: Session[]
}

export type MachineWorkspace = { id: string; name: string; directory: string }

function decodeMachineWorkspace(value: unknown): MachineWorkspace | undefined {
  if (value === undefined || value === null) return undefined
  const workspace = jsonObject(value, "Server returned an invalid machine workspace")
  if (typeof workspace.id !== "string" || !workspace.id) throw new Error("Server returned an invalid machine workspace")
  return {
    id: workspace.id,
    name: typeof workspace.name === "string" && workspace.name ? workspace.name : "Kettle Oracle",
    directory: typeof workspace.directory === "string" ? workspace.directory : "",
  }
}

function decodeRuntimeBootstrap(value: unknown): RuntimeBootstrap {
  const root = jsonObject(value, "Server returned invalid runtime bootstrap")
  return {
    healthy: root.healthy === true,
    path: jsonObject(root.path, "Server returned invalid runtime path") as RuntimePath,
    projects: jsonArray(root.projects, "Server returned invalid runtime projects") as Project[],
    config: jsonObject(root.config, "Server returned invalid runtime config") as Config,
    machineWorkspace: decodeMachineWorkspace(root.machineWorkspace),
    oracles: root.oracles === undefined ? [] : jsonArray(root.oracles, "Server returned invalid workspace Oracles") as Session[],
  }
}

export async function getRuntimeBootstrap(server: ServerConnection.HttpBase) {
  return expectJson(
    await serverFetch(server, "/global/bootstrap"),
    decodeRuntimeBootstrap,
    { error: "Runtime bootstrap request failed" },
  )
}
